import { useEffect, useState } from 'react';
import { RefreshCw, Eye, EyeOff } from 'lucide-react';
import { api, setToken, type AuthUser } from '../api';
import Logo from '../components/Logo';

type LoginResp = {
  token: string;
  user: { id: number; username: string; email: string; role: 'admin'|'staff'; must_change_password: boolean }
};
type CaptchaResp = { id: string; svg: string; expires_in: number };

const ERROR_MAP: Record<string, string> = {
  invalid_credentials: '아이디 또는 비밀번호가 올바르지 않습니다.',
  captcha_failed: '캡차가 일치하지 않습니다. 새 캡차로 다시 시도해 주세요.',
  too_many_attempts: '로그인 시도가 너무 많습니다. 잠시 후 다시 시도해 주세요.',
  validation: '입력값이 올바르지 않습니다.',
};

export default function Login({ onLogin }: { onLogin: (u: AuthUser) => void }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [captcha, setCaptcha] = useState<CaptchaResp | null>(null);
  const [captchaAns, setCaptchaAns] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const reloadCaptcha = async () => {
    setCaptchaAns('');
    try {
      const c = await api<CaptchaResp>('GET', '/auth/captcha');
      setCaptcha(c);
    } catch {
      setCaptcha(null);
    }
  };
  useEffect(() => { reloadCaptcha(); }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!captcha) { setErr('캡차를 불러오는 중입니다.'); return; }
    setLoading(true);
    try {
      const r = await api<LoginResp>('POST', '/auth/login', {
        username: username.trim(),
        password,
        captcha_id: captcha.id,
        captcha_answer: captchaAns.trim(),
      });
      setToken(r.token);
      const me = await api<{ user: AuthUser }>('GET', '/auth/me');
      onLogin(me.user);
    } catch (e: any) {
      setErr(ERROR_MAP[e?.message] ?? (e?.message ?? '로그인 실패'));
      reloadCaptcha();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-screen">
      <form className="login-card" onSubmit={submit}>
        <div className="login-brand">
          <Logo size={56} />
          <h1>서포트리 정산</h1>
        </div>

        <div>
          <label>아이디</label>
          <input value={username} onChange={e => setUsername(e.target.value)} required autoFocus
                 autoComplete="username" placeholder="아이디" />
        </div>
        <div>
          <label>비밀번호</label>
          <div style={{ position: 'relative' }}>
            <input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                   required autoComplete="current-password" placeholder="비밀번호"
                   style={{ width: '100%', paddingRight: 36 }} />
            <button type="button" onClick={() => setShowPw(v => !v)}
                    aria-label={showPw ? '비밀번호 숨기기' : '비밀번호 표시'}
                    title={showPw ? '비밀번호 숨기기' : '비밀번호 표시'}
                    style={{
                      position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                      background: 'transparent', border: 0, cursor: 'pointer',
                      color: 'var(--text-muted)', padding: 6, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
              {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>
        <div>
          <label>보안 문자</label>
          <div className="captcha-box">
            <div className="captcha-img" dangerouslySetInnerHTML={{ __html: captcha?.svg ?? '' }} />
            <input value={captchaAns} onChange={e => setCaptchaAns(e.target.value.toUpperCase())}
                   required maxLength={8} placeholder="이미지 글자 입력" />
            <button type="button" className="reload" onClick={reloadCaptcha} title="새로고침">
              <RefreshCw size={15} />
            </button>
          </div>
        </div>

        {err && <div className="error">{err}</div>}
        <button type="submit" className="btn" disabled={loading || !captcha}>
          {loading ? '확인 중…' : '로그인'}
        </button>
      </form>
    </div>
  );
}
